// Per-column DISPLAY FORMATS for transaction list/table cells.
//
// A column may carry an optional `display: DisplayFormat` config (set in the
// transaction designer's "Display as" panel). It is display-only — it never
// affects editing or the stored value. This module is the single source of
// truth shared by every list/table surface (designer live-preview ListView,
// nested ChildFormTab grid, and the downloaded-runtime EmbedView), so the
// look is identical everywhere.
//
// `DisplayCell` renders the rich/visual formats (rings, bars, badges, dots,
// links, micro-charts, composites). `displayPlainText` returns a string for
// non-React contexts (tooltips, CSV, structural mocks).

import { useState } from 'react'

import type { ColumnDef } from './types'
import { personLabel, usePeople } from './people'
import { columnFormKey, richCellText } from './utils'
import { encodeQr } from './qrlite'
import { flagEmoji, resolveCountry, countryFromPhone } from './countryData'
import { elapsedMs, elapsedTone, formatElapsed, TONE_COLOR, useNow } from '../live/clock'

// ── Config shape ────────────────────────────────────────────────────────────

export type DisplayFormatType =
  | 'text'            // default — no special formatting
  | 'number'          // decimal / currency / accounting / percent / scientific / compact
  | 'percent_ring'    // circular completeness ring around the value
  | 'progress'        // horizontal progress bar (value within min..max)
  | 'data_bar'        // in-cell bar drawn behind the number
  | 'heatmap'         // text/background graded across value bands (stepped or gradient)
  | 'rating'          // stars / hearts / dots out of N
  | 'signed'          // +/- delta, colored, with ▲▼ arrow
  | 'sparkline'       // tiny inline line chart from an array / comma list
  | 'bullet'          // target-vs-actual bullet (actual bar + target tick)
  | 'status_tag'      // colored badge/pill (value → color map)
  | 'status_dot'      // colored dot; value on hover (optionally hidden)
  | 'icon_map'        // value → icon glyph
  | 'boolean'         // ✓ / ✗ (or a value map)
  | 'tags'            // comma/array value → colored chips
  | 'avatar'          // initials avatar + name (+ secondary line)
  | 'background'      // conditional cell background from first-matching rule
  | 'date'            // date/time pattern
  | 'relative_time'   // "3 days ago" / "in 2h"
  | 'date_range'      // start + end companion column shown as one cell
  | 'countdown'       // time remaining to a due date, SLA-colored
  | 'elapsed'         // live clock since a start time (m:ss / h:mm), amber/red by minutes
  | 'telephone'       // phone icon, click-to-call (tel:) — optional country flag from dial code
  | 'country'         // country flag + name from an ISO code / dial code / name
  | 'email'           // envelope icon, click to mail (mailto:)
  | 'url'             // open link in a new tab
  | 'image'           // thumbnail of an image URL
  | 'file'            // filename + type icon, click to open
  | 'qr'              // render the value as a scannable QR code
  | 'map'             // map pin opening the location in a new tab
  | 'copy'            // value with a copy-to-clipboard affordance
  | 'case'            // text case transform (upper/lower/title/camel/sentence)
  | 'truncate'        // clamp long text with a hover tooltip
  | 'masked'          // mask sensitive value (••••1234), reveal on click
  | 'template'        // wrap value in a template, e.g. "INV-{value}"
  | 'two_line'        // bold primary value + muted secondary companion column

export interface DisplayMapEntry {
  when: string                 // matched (case-insensitively) against the value
  color?: string               // named color or hex/css
  icon?: string                // emoji / glyph, or a common name (circle, check, x, …) mapped via resolveIconGlyph
  label?: string               // replacement display text
}

export interface DisplayRule {
  when: string                 // expression over columns, e.g. "(budget-actual)/budget > 1.2"
  color: string                // applied when the expression is truthy
}

export interface DisplayFormat {
  type: DisplayFormatType

  // numeric formatting (number / data_bar / signed / etc.)
  numberStyle?: 'decimal' | 'currency' | 'accounting' | 'percent' | 'scientific' | 'compact'
  locale?: string              // e.g. 'en-IN', 'en-US'
  currency?: string            // ISO code, e.g. 'INR', 'USD'
  decimals?: number
  prefix?: string
  suffix?: string

  // progress / ring / bullet / data_bar / rating
  min?: number                 // default 0
  max?: number                 // default 100 (rating: 5)
  maxColumn?: string           // companion column holding the max/target value
  color?: string               // bar/ring/fill base color
  showValue?: boolean          // overlay the numeric value (default true)

  // heatmap
  scale?: 'stepped' | 'gradient'
  thresholds?: number[]        // stepped band boundaries (ascending)
  colors?: string[]            // band colors (stepped: thresholds.length+1; gradient: [low, high])
  applyTo?: 'text' | 'background'

  // status maps / conditional background
  map?: DisplayMapEntry[]      // value → color/icon/label
  rules?: DisplayRule[]        // first matching rule wins (background)
  defaultColor?: string
  defaultIcon?: string
  defaultLabel?: string
  hideValue?: boolean          // status_dot / icon_map: show only the indicator, value on hover

  // rating / icons
  icon?: string                // 'star' | 'heart' | 'circle' | any emoji

  // text transforms
  textCase?: 'upper' | 'lower' | 'title' | 'camel' | 'sentence'
  truncate?: number            // max characters before ellipsis
  maskLast?: number            // masked: number of trailing chars left visible
  maskChar?: string            // masked: fill character (default '•')
  template?: string            // template: pattern with {value} / {col_name} tokens

  // temporal
  datePattern?: string         // Angular-DatePipe-style, e.g. 'dd-MMM-yyyy'
  endColumn?: string           // date_range: companion end-date column
  warnDays?: number            // countdown: amber threshold
  dangerDays?: number          // countdown: red threshold
  warnAfterMinutes?: number    // elapsed: amber threshold
  dangerAfterMinutes?: number  // elapsed: red threshold
  warnColumn?: string          // elapsed: amber threshold (minutes) read off the row
  dangerColumn?: string        // elapsed: red threshold (minutes) read off the row
  elapsedFormat?: 'auto' | 'mm:ss' | 'h:mm'   // elapsed (endColumn stops the clock)

  // contact / link / media
  label?: string               // url / telephone / email / file: display label override
  hrefColumn?: string          // url: companion column holding the href when value is the label
  showFlag?: boolean           // telephone: prepend the country flag from the dial code (default true)

  // country
  countryShow?: 'flag_name' | 'flag' | 'flag_code'  // country: what to render (default flag + name)

  // composite
  secondaryColumn?: string     // two_line / avatar: muted secondary value
  avatarColumn?: string        // avatar: column whose value seeds the picture/initials
}

// ── Small theme-agnostic style tokens (work on both --color-* and --pg-*) ────

const C = {
  accent: 'var(--color-accent, var(--pg-accent, #2563eb))',
  border: 'var(--color-border, var(--pg-border, #e5e7eb))',
  muted: 'var(--color-text-secondary, var(--pg-text-secondary, #6b7280))',
  text: 'var(--color-text, var(--pg-text, #111827))',
  surface: 'var(--color-surface, var(--pg-surface, #ffffff))',
  track: 'var(--color-border, var(--pg-border, #e5e7eb))',
}

const NAMED_COLORS: Record<string, string> = {
  green: '#16a34a', red: '#dc2626', yellow: '#eab308', amber: '#f59e0b',
  orange: '#ea580c', blue: '#2563eb', grey: '#9ca3af', gray: '#9ca3af',
  purple: '#7c3aed', pink: '#ec4899', teal: '#14b8a6', cyan: '#06b6d4',
  black: '#111827', white: '#ffffff',
}

function resolveColor(c?: string | null): string {
  if (!c) return C.muted
  const key = String(c).trim().toLowerCase()
  return NAMED_COLORS[key] || String(c).trim()
}

function softBg(color: string): string {
  return `color-mix(in srgb, ${color} 16%, transparent)`
}

// ── Value helpers ────────────────────────────────────────────────────────────

function toNum(v: unknown): number | null {
  if (v == null || v === '') return null
  const n = typeof v === 'number' ? v : Number(String(v).replace(/[, ]/g, ''))
  return Number.isFinite(n) ? n : null
}

function clamp01(n: number): number {
  return n < 0 ? 0 : n > 1 ? 1 : n
}

const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const DAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function parseDate(v: unknown): Date | null {
  if (v == null || v === '') return null
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v
  const raw = String(v).trim()
  // A bare `yyyy-MM-dd` is a calendar day, not an instant: `new Date()` would
  // read it as UTC midnight and show the previous day west of Greenwich.
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw)
  const d = day ? new Date(+day[1], +day[2] - 1, +day[3]) : new Date(raw)
  return Number.isNaN(d.getTime()) ? null : d
}

// How a DATE / DATETIME / TIME column reads when the author never gave it a
// display format: the BROWSER's locale decides (order, month names, 12/24-hour
// clock). Without this a grid shows the wire form — `2026-10-09T23:10:34.922066`
// — which is the stored value, not a date. An authored `datePattern` still wins
// (DisplayCell handles those columns before this is ever asked).
const _localeFormatters = new Map<string, Intl.DateTimeFormat | null>()
function localeFormatter(kind: 'date' | 'datetime' | 'time'): Intl.DateTimeFormat | null {
  if (!_localeFormatters.has(kind)) {
    const opts: Intl.DateTimeFormatOptions =
      kind === 'date' ? { dateStyle: 'medium' }
        : kind === 'datetime' ? { dateStyle: 'medium', timeStyle: 'short' }
          : { timeStyle: 'short' }
    let f: Intl.DateTimeFormat | null = null
    try { f = new Intl.DateTimeFormat(undefined, opts) } catch { /* fixed pattern below */ }
    _localeFormatters.set(kind, f)
  }
  return _localeFormatters.get(kind) ?? null
}

/** Readable text for an UNFORMATTED date/datetime/time column's value in the
 *  browser's locale, or null when the column is not temporal (callers fall
 *  through to their own formatting). A value that does not parse is returned
 *  as it came. */
export function temporalCellText(value: unknown, col?: ColumnDef): string | null {
  if (!col || value == null || value === '') return null
  const t = (col.col_type || '').toString().trim().toUpperCase()
  let kind: 'date' | 'datetime' | 'time'
  let d: Date | null
  if (t === 'DATE') { kind = 'date'; d = parseDate(value) }
  else if (t === 'DATETIME' || t === 'TIMESTAMP' || t === 'TIMESTAMPTZ') { kind = 'datetime'; d = parseDate(value) }
  else if (t === 'TIME') {
    kind = 'time'
    const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(String(value).trim())
    d = m ? new Date(2000, 0, 1, +m[1], +m[2], +(m[3] || 0)) : null
  } else return null
  if (!d) return String(value)
  const f = localeFormatter(kind)
  if (f) return f.format(d)
  return kind === 'time' ? formatDateValue(d, 'HH:mm')
    : formatDateValue(d, kind === 'date' ? 'dd-MMM-yyyy' : 'dd-MMM-yyyy HH:mm')
}

export function formatDateValue(v: unknown, pattern?: string | null): string {
  const d = parseDate(v)
  if (!d) return v == null ? '' : String(v)
  const pat = pattern && String(pattern).trim() ? String(pattern) : 'dd-MMM-yyyy'
  const pad = (n: number) => String(n).padStart(2, '0')
  const h12 = d.getHours() % 12 || 12
  const tokens: Record<string, string> = {
    yyyy: String(d.getFullYear()), yy: String(d.getFullYear()).slice(-2),
    MMMM: MONTHS_LONG[d.getMonth()], MMM: MONTHS_SHORT[d.getMonth()],
    MM: pad(d.getMonth() + 1), M: String(d.getMonth() + 1),
    dd: pad(d.getDate()), d: String(d.getDate()),
    EEEE: DAYS_LONG[d.getDay()], EEE: DAYS_SHORT[d.getDay()],
    HH: pad(d.getHours()), H: String(d.getHours()),
    hh: pad(h12), h: String(h12),
    mm: pad(d.getMinutes()), ss: pad(d.getSeconds()),
    a: d.getHours() < 12 ? 'AM' : 'PM',
  }
  return pat.replace(/yyyy|yy|MMMM|MMM|MM|M|dd|d|EEEE|EEE|HH|H|hh|h|mm|ss|a/g, (t) => tokens[t] ?? t)
}

export function formatNumberValue(v: unknown, fmt: DisplayFormat): string {
  const n = toNum(v)
  if (n == null) return v == null ? '' : String(v)
  const style = fmt.numberStyle || 'decimal'
  const locale = fmt.locale || undefined
  const opts: Intl.NumberFormatOptions = {}
  if (fmt.decimals != null) { opts.minimumFractionDigits = fmt.decimals; opts.maximumFractionDigits = fmt.decimals }
  else opts.maximumFractionDigits = 4
  let body: string
  try {
    switch (style) {
      case 'currency':
        body = n.toLocaleString(locale, { ...opts, style: 'currency', currency: fmt.currency || 'USD' }); break
      case 'accounting':
        body = n.toLocaleString(locale, { ...opts, style: 'currency', currency: fmt.currency || 'USD', currencySign: 'accounting' }); break
      case 'percent':
        body = n.toLocaleString(locale, { ...opts, style: 'percent' }); break
      case 'scientific':
        body = n.toExponential(fmt.decimals ?? 2); break
      case 'compact':
        body = n.toLocaleString(locale, { ...opts, notation: 'compact', compactDisplay: 'short' }); break
      default:
        body = n.toLocaleString(locale, opts)
    }
  } catch {
    body = n.toLocaleString(undefined, opts)
  }
  return `${fmt.prefix || ''}${body}${fmt.suffix || ''}`
}

function transformCase(s: string, mode: DisplayFormat['textCase']): string {
  switch (mode) {
    case 'upper': return s.toUpperCase()
    case 'lower': return s.toLowerCase()
    case 'title': return s.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    case 'sentence': return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
    case 'camel': {
      const parts = s.split(/[\s_\-]+/).filter(Boolean)
      if (!parts.length) return s
      return parts.map((p, i) => i === 0 ? p.toLowerCase() : p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join('')
    }
    default: return s
  }
}

// ── Companion-column resolution ──────────────────────────────────────────────

// Read a value from the row by a column reference (name / db_name / col_id).
/** The `elapsed` format: a clock that ticks while the row is open, stopped by
 *  `endColumn` once it holds a time; thresholds in minutes from the format or
 *  (per row) from `warnColumn` / `dangerColumn`. The shared clock (`live/clock`)
 *  is the same one the smart-page `timer` block uses. */
function ElapsedCell({ value, fmt, row, columns }: {
  value: unknown; fmt: DisplayFormat; row?: Record<string, unknown>; columns?: ColumnDef[]
}): React.ReactElement {
  const end = readCompanion(fmt.endColumn, row, columns)
  const stopped = end != null && end !== ''
  const now = useNow(stopped ? false : 1000)
  const ms = elapsedMs(value, now, stopped ? end : undefined)
  if (ms == null) return <span style={{ color: C.muted }}>—</span>
  const warn = fmt.warnColumn ? readCompanion(fmt.warnColumn, row, columns) : fmt.warnAfterMinutes
  const danger = fmt.dangerColumn ? readCompanion(fmt.dangerColumn, row, columns) : fmt.dangerAfterMinutes
  const tone = elapsedTone(ms, warn, danger)
  return (
    <span
      title={formatDateValue(value, 'dd-MMM-yyyy HH:mm')}
      style={{ color: TONE_COLOR[tone], fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
    >
      {formatElapsed(ms, fmt.elapsedFormat || 'auto')}
    </span>
  )
}

function readCompanion(
  ref: string | undefined,
  row: Record<string, unknown> | undefined,
  columns: ColumnDef[] | undefined,
): unknown {
  if (!ref || !row) return undefined
  if (ref in row) return row[ref]
  const lc = ref.toLowerCase()
  const col = (columns || []).find(
    (c) => c.name?.toLowerCase() === lc || c.db_name?.toLowerCase() === lc || c.col_id?.toLowerCase() === lc,
  )
  if (col) {
    if (col.db_name && col.db_name in row) return row[col.db_name]
    if (col.name && col.name in row) return row[col.name]
  }
  // Last resort: case-insensitive key match on the row.
  const hit = Object.keys(row).find((k) => k.toLowerCase() === lc)
  return hit ? row[hit] : undefined
}

// ── Safe expression evaluator (for `background` rules) ───────────────────────
//
// Supports numbers, quoted strings, column identifiers, + - * / %, parentheses,
// comparisons (< > <= >= == !=) and && || . No eval / Function — a tiny
// recursive-descent parser over a whitelisted token set.

type Scalar = number | string | boolean

function buildScope(
  value: unknown, row: Record<string, unknown> | undefined, columns: ColumnDef[] | undefined,
): Record<string, unknown> {
  const scope: Record<string, unknown> = {}
  if (row) for (const k of Object.keys(row)) scope[k.toLowerCase()] = row[k]
  for (const c of columns || []) {
    const v = row ? readCompanion(c.db_name || c.name, row, columns) : undefined
    if (c.name) scope[c.name.toLowerCase()] = v
    if (c.db_name) scope[c.db_name.toLowerCase()] = v
  }
  scope['value'] = value
  return scope
}

function tokenizeExpr(src: string): string[] {
  const tokens: string[] = []
  const re = /\s*(>=|<=|==|!=|&&|\|\||[-+*/%()<>]|"[^"]*"|'[^']*'|[A-Za-z_][A-Za-z0-9_.]*|\d+(?:\.\d+)?)/y
  let m: RegExpExecArray | null
  let pos = 0
  while (pos < src.length) {
    re.lastIndex = pos
    m = re.exec(src)
    if (!m) break
    tokens.push(m[1])
    pos = re.lastIndex
  }
  return tokens
}

function evalExpr(src: string, scope: Record<string, unknown>): Scalar {
  const tokens = tokenizeExpr(src)
  let i = 0
  const peek = () => tokens[i]
  const next = () => tokens[i++]

  const toNumber = (v: Scalar): number => typeof v === 'number' ? v : Number(v)

  function parseOr(): Scalar {
    let left = parseAnd()
    while (peek() === '||') { next(); const r = parseAnd(); left = (!!left || !!r) }
    return left
  }
  function parseAnd(): Scalar {
    let left = parseCmp()
    while (peek() === '&&') { next(); const r = parseCmp(); left = (!!left && !!r) }
    return left
  }
  function parseCmp(): Scalar {
    let left = parseAdd()
    const op = peek()
    if (op === '<' || op === '>' || op === '<=' || op === '>=' || op === '==' || op === '!=') {
      next()
      const right = parseAdd()
      const bothNum = typeof left !== 'string' && typeof right !== 'string'
      const a = bothNum ? toNumber(left as Scalar) : String(left)
      const b = bothNum ? toNumber(right as Scalar) : String(right)
      switch (op) {
        case '<': return a < b
        case '>': return a > b
        case '<=': return a <= b
        case '>=': return a >= b
        case '==': return a === b
        case '!=': return a !== b
      }
    }
    return left
  }
  function parseAdd(): Scalar {
    let left = parseMul()
    while (peek() === '+' || peek() === '-') {
      const op = next()
      const right = parseMul()
      left = op === '+' ? toNumber(left) + toNumber(right) : toNumber(left) - toNumber(right)
    }
    return left
  }
  function parseMul(): Scalar {
    let left = parseUnary()
    while (peek() === '*' || peek() === '/' || peek() === '%') {
      const op = next()
      const right = parseUnary()
      const a = toNumber(left), b = toNumber(right)
      left = op === '*' ? a * b : op === '/' ? (b === 0 ? 0 : a / b) : (b === 0 ? 0 : a % b)
    }
    return left
  }
  function parseUnary(): Scalar {
    if (peek() === '-') { next(); return -toNumber(parseUnary()) }
    if (peek() === '+') { next(); return toNumber(parseUnary()) }
    return parsePrimary()
  }
  function parsePrimary(): Scalar {
    const t = next()
    if (t === '(') { const v = parseOr(); if (peek() === ')') next(); return v }
    if (t == null) return 0
    if ((t.startsWith('"') && t.endsWith('"')) || (t.startsWith("'") && t.endsWith("'"))) return t.slice(1, -1)
    if (/^\d/.test(t)) return Number(t)
    const raw = scope[t.toLowerCase()]
    const num = toNum(raw)
    return num != null ? num : (raw == null ? '' : String(raw))
  }

  try {
    return parseOr()
  } catch {
    return false
  }
}

// ── Map / rule matching ──────────────────────────────────────────────────────

function matchMapEntry(value: unknown, map?: DisplayMapEntry[]): DisplayMapEntry | undefined {
  if (!map || !map.length) return undefined
  const s = value == null ? '' : String(value).trim().toLowerCase()
  return map.find((e) => String(e.when).trim().toLowerCase() === s)
}

// ── Icon glyph resolution ────────────────────────────────────────────────────
// `icon` is rendered as literal text, so it must be a glyph/emoji character.
// Authors (and AI agents) often write icon NAMES instead — map the common ones
// to glyphs, and drop anything else that still looks like a name so a raw word
// never renders inside a cell.

const ICON_GLYPHS: Record<string, string> = {
  circle: '●', dot: '●', bullet: '●',
  check: '✓', tick: '✓', done: '✓', success: '✓', 'check-circle': '✓',
  x: '✕', cross: '✕', close: '✕', fail: '✕', 'x-circle': '✕',
  warning: '⚠', warn: '⚠', alert: '⚠', 'alert-triangle': '⚠',
  info: 'ℹ', star: '★', heart: '❤', flag: '⚑',
  clock: '◷', pending: '◷', time: '◷', hourglass: '⧗',
  up: '▲', 'arrow-up': '▲', increase: '▲',
  down: '▼', 'arrow-down': '▼', decrease: '▼',
  minus: '–', dash: '–', pause: '❚❚', ban: '⊘', block: '⊘',
  lock: '🔒', unlock: '🔓', bolt: '⚡', lightning: '⚡',
  question: '?', bell: '🔔', mail: '✉', phone: '☎', pin: '📍',
}

export function resolveIconGlyph(icon?: string): string {
  const s = (icon || '').trim()
  if (!s) return ''
  const named = ICON_GLYPHS[s.toLowerCase()]
  if (named) return named
  // An unknown ASCII identifier is an icon name we can't map — hide it rather
  // than print a word in the cell. Anything else (emoji, ✓, single char) is
  // already a glyph and passes through.
  if (/^[a-z][a-z0-9 _-]+$/i.test(s)) return ''
  return s
}

// ── Plain-text fallback (tooltips, CSV, structural mocks) ─────────────────────

export function displayPlainText(
  value: unknown, col?: ColumnDef, row?: Record<string, unknown>, columns?: ColumnDef[],
): string {
  const fmt = col?.display
  const rich = richCellText(value, col)
  if (rich != null) return rich
  if (!fmt || !fmt.type || fmt.type === 'text') return value == null ? '' : String(value)
  switch (fmt.type) {
    case 'number': case 'data_bar': case 'percent_ring': case 'progress': case 'signed':
      return formatNumberValue(value, fmt)
    case 'date': case 'countdown': return formatDateValue(value, fmt.datePattern)
    case 'elapsed': {
      const ms = elapsedMs(value, Date.now(), readCompanion(fmt.endColumn, row, columns))
      return ms == null ? '' : formatElapsed(ms, fmt.elapsedFormat || 'auto')
    }
    case 'date_range': {
      const end = readCompanion(fmt.endColumn, row, columns)
      return `${formatDateValue(value, fmt.datePattern)} – ${formatDateValue(end, fmt.datePattern)}`
    }
    case 'status_tag': case 'status_dot': case 'icon_map': {
      const e = matchMapEntry(value, fmt.map)
      return e?.label || (value == null ? '' : String(value))
    }
    case 'country': {
      const c = resolveCountry(value)
      return c ? c.name : (value == null ? '' : String(value))
    }
    case 'case': return transformCase(value == null ? '' : String(value), fmt.textCase)
    case 'template': return applyTemplate(fmt.template || '{value}', value, row, columns)
    case 'masked': return maskValue(value == null ? '' : String(value), fmt)
    default:
      return value == null ? '' : String(value)
  }
}

function applyTemplate(
  tpl: string, value: unknown, row?: Record<string, unknown>, columns?: ColumnDef[],
): string {
  return tpl.replace(/\{([^}]+)\}/g, (_m, key) => {
    const k = String(key).trim()
    if (k.toLowerCase() === 'value') return value == null ? '' : String(value)
    const v = readCompanion(k, row, columns)
    return v == null ? '' : String(v)
  })
}

function maskValue(s: string, fmt: DisplayFormat): string {
  const last = fmt.maskLast ?? 4
  const ch = fmt.maskChar || '•'
  if (s.length <= last) return s
  return ch.repeat(Math.min(s.length - last, 8)) + s.slice(-last)
}

// ── Whether a column has an active display format ────────────────────────────

export function hasDisplayFormat(col?: ColumnDef): boolean {
  return !!col?.display && !!col.display.type && col.display.type !== 'text'
}

// ── The renderer ─────────────────────────────────────────────────────────────

export interface DisplayCellProps {
  value: unknown
  col: ColumnDef
  row?: Record<string, unknown>
  columns?: ColumnDef[]
}

export function DisplayCell({ value, col, row, columns }: DisplayCellProps): React.ReactElement {
  const fmt = col.display as DisplayFormat
  const empty = value == null || value === ''
  // A user-key column reads as the PERSON, ahead of every configured format:
  // the stored value is an identifier (see `txn/people`), so there is nothing
  // for a number/date/badge format to say about it. Only when the engine named
  // the key — an unnamed one falls through and renders as it always did.
  const person = personLabel(usePeople(), columnFormKey(col), value)
  if (person) return <span>{person}</span>

  switch (fmt.type) {
    case 'number':
      return <span>{formatNumberValue(value, fmt)}</span>

    case 'percent_ring': {
      const n = toNum(value)
      const max = fmt.maxColumn ? toNum(readCompanion(fmt.maxColumn, row, columns)) ?? fmt.max ?? 100 : fmt.max ?? 100
      const pct = n == null ? 0 : clamp01(n / (max || 100))
      const color = resolveColor(fmt.color || C.accent)
      const r = 9, circ = 2 * Math.PI * r
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <svg width={24} height={24} viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
            <circle cx={12} cy={12} r={r} fill="none" stroke={C.track} strokeWidth={3} />
            <circle cx={12} cy={12} r={r} fill="none" stroke={color} strokeWidth={3}
              strokeDasharray={`${circ * pct} ${circ}`} strokeLinecap="round"
              transform="rotate(-90 12 12)" />
          </svg>
          {fmt.showValue !== false && <span>{n == null ? '' : `${Math.round(pct * 100)}%`}</span>}
        </span>
      )
    }

    case 'progress': {
      const n = toNum(value)
      const min = fmt.min ?? 0
      const max = fmt.maxColumn ? toNum(readCompanion(fmt.maxColumn, row, columns)) ?? fmt.max ?? 100 : fmt.max ?? 100
      const pct = n == null ? 0 : clamp01((n - min) / ((max - min) || 1))
      const color = resolveColor(fmt.color || C.accent)
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 90 }}>
          <span style={{ flex: 1, height: 8, borderRadius: 999, background: C.track, overflow: 'hidden', minWidth: 48 }}>
            <span style={{ display: 'block', height: '100%', width: `${pct * 100}%`, background: color, borderRadius: 999 }} />
          </span>
          {fmt.showValue !== false && <span style={{ fontSize: '0.82em', color: C.muted }}>{n == null ? '' : `${Math.round(pct * 100)}%`}</span>}
        </span>
      )
    }

    case 'data_bar': {
      const n = toNum(value)
      const min = fmt.min ?? 0
      const max = fmt.max ?? 100
      const pct = n == null ? 0 : clamp01((n - min) / ((max - min) || 1))
      const color = resolveColor(fmt.color || C.accent)
      return (
        <span style={{ position: 'relative', display: 'inline-block', minWidth: 70, padding: '1px 4px' }}>
          <span style={{ position: 'absolute', inset: 0, width: `${pct * 100}%`, background: softBg(color), borderRadius: 3 }} />
          <span style={{ position: 'relative' }}>{formatNumberValue(value, fmt)}</span>
        </span>
      )
    }

    case 'heatmap': {
      const n = toNum(value)
      const color = heatmapColor(n, fmt)
      const applyBg = fmt.applyTo === 'background'
      const txt = formatNumberValue(value, fmt)
      return (
        <span style={applyBg
          ? { display: 'inline-block', padding: '2px 8px', borderRadius: 4, background: softBg(color), color: C.text }
          : { color, fontWeight: 600 }}>
          {txt}
        </span>
      )
    }

    case 'rating': {
      const n = Math.round(toNum(value) ?? 0)
      const max = fmt.max ?? 5
      const glyphFull = fmt.icon === 'heart' ? '❤' : fmt.icon === 'circle' ? '●' : '★'
      const glyphEmpty = fmt.icon === 'heart' ? '🤍' : fmt.icon === 'circle' ? '○' : '☆'
      const color = resolveColor(fmt.color || '#f59e0b')
      return (
        <span title={`${n}/${max}`} style={{ color, letterSpacing: 1 }}>
          {Array.from({ length: max }, (_, i) => (i < n ? glyphFull : glyphEmpty)).join('')}
        </span>
      )
    }

    case 'signed': {
      const n = toNum(value)
      if (n == null) return <span />
      const color = n > 0 ? NAMED_COLORS.green : n < 0 ? NAMED_COLORS.red : C.muted
      const arrow = n > 0 ? '▲' : n < 0 ? '▼' : ''
      const body = formatNumberValue(Math.abs(n), fmt)
      return <span style={{ color, fontWeight: 600 }}>{arrow} {n > 0 ? '+' : n < 0 ? '−' : ''}{body}</span>
    }

    case 'sparkline': {
      const series = parseSeries(value)
      if (!series.length) return <span style={{ color: C.muted }}>—</span>
      const w = 64, h = 18, lo = Math.min(...series), hi = Math.max(...series)
      const span = hi - lo || 1
      const pts = series.map((v, i) => `${(i / (series.length - 1 || 1)) * w},${h - ((v - lo) / span) * h}`).join(' ')
      return (
        <svg width={w} height={h} style={{ verticalAlign: 'middle' }}>
          <polyline points={pts} fill="none" stroke={resolveColor(fmt.color || C.accent)} strokeWidth={1.5} />
        </svg>
      )
    }

    case 'bullet': {
      const actual = toNum(value) ?? 0
      const target = toNum(readCompanion(fmt.maxColumn, row, columns)) ?? fmt.max ?? 0
      const scaleMax = Math.max(actual, target) * 1.1 || 1
      const aPct = clamp01(actual / scaleMax)
      const tPct = clamp01(target / scaleMax)
      const ok = target > 0 && actual >= target
      const color = resolveColor(fmt.color || (ok ? NAMED_COLORS.green : C.accent))
      return (
        <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2, minWidth: 110 }}>
          <span style={{ fontSize: '0.78em', color: C.muted }}>
            {formatNumberValue(actual, fmt)} / {formatNumberValue(target, fmt)}
          </span>
          <span style={{ position: 'relative', height: 8, borderRadius: 999, background: C.track }}>
            <span style={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${aPct * 100}%`, background: color, borderRadius: 999 }} />
            <span style={{ position: 'absolute', left: `${tPct * 100}%`, top: -2, height: 12, width: 2, background: C.text }} />
          </span>
        </span>
      )
    }

    case 'status_tag': {
      const e = matchMapEntry(value, fmt.map)
      const color = resolveColor(e?.color || fmt.defaultColor)
      const label = e?.label || (empty ? '' : String(value))
      const icon = resolveIconGlyph(e?.icon)
      if (empty && !label) return <span />
      return (
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px',
          borderRadius: 999, background: softBg(color), color, fontWeight: 600, fontSize: '0.82em',
        }}>
          {icon && <span>{icon}</span>}{label}
        </span>
      )
    }

    case 'status_dot': {
      const e = matchMapEntry(value, fmt.map)
      const color = resolveColor(e?.color || fmt.defaultColor)
      const label = e?.label || (empty ? '' : String(value))
      return (
        <span title={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 10, height: 10, borderRadius: 999, background: color, flexShrink: 0, boxShadow: '0 0 0 2px ' + softBg(color) }} />
          {!fmt.hideValue && <span>{label}</span>}
        </span>
      )
    }

    case 'icon_map': {
      const e = matchMapEntry(value, fmt.map)
      const icon = resolveIconGlyph(e?.icon || fmt.defaultIcon)
      return (
        <span title={empty ? '' : String(value)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: resolveColor(e?.color) }}>{icon}</span>
          {!fmt.hideValue && <span>{e?.label || (empty ? '' : String(value))}</span>}
        </span>
      )
    }

    case 'boolean': {
      const truthy = value === true || value === 1 || value === '1' || String(value).toLowerCase() === 'true' || String(value).toLowerCase() === 'yes'
      const e = matchMapEntry(value, fmt.map)
      if (e) return <span style={{ color: resolveColor(e.color) }}>{resolveIconGlyph(e.icon) || e.label || String(value)}</span>
      return <span style={{ color: truthy ? NAMED_COLORS.green : C.muted, fontWeight: 600 }}>{truthy ? '✓' : '✗'}</span>
    }

    case 'tags': {
      const parts = Array.isArray(value) ? value.map(String) : String(value ?? '').split(/[,;]/).map((s) => s.trim()).filter(Boolean)
      if (!parts.length) return <span />
      return (
        <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
          {parts.map((p, i) => {
            const e = matchMapEntry(p, fmt.map)
            const color = resolveColor(e?.color || fmt.defaultColor || C.accent)
            return (
              <span key={i} style={{ padding: '1px 7px', borderRadius: 999, background: softBg(color), color, fontSize: '0.8em', fontWeight: 600 }}>
                {e?.label || p}
              </span>
            )
          })}
        </span>
      )
    }

    case 'avatar': {
      const name = String(readCompanion(fmt.avatarColumn, row, columns) ?? value ?? '').trim()
      const secondary = fmt.secondaryColumn ? readCompanion(fmt.secondaryColumn, row, columns) : undefined
      const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?'
      const bg = avatarColor(name)
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 24, height: 24, borderRadius: 999, background: bg, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7em', fontWeight: 700, flexShrink: 0 }}>
            {initials}
          </span>
          <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.15 }}>
            <span>{name}</span>
            {secondary != null && secondary !== '' && <span style={{ fontSize: '0.78em', color: C.muted }}>{String(secondary)}</span>}
          </span>
        </span>
      )
    }

    case 'background': {
      const scope = buildScope(value, row, columns)
      let color: string | undefined
      for (const rule of fmt.rules || []) {
        if (evalExpr(rule.when, scope)) { color = resolveColor(rule.color); break }
      }
      if (!color && fmt.defaultColor) color = resolveColor(fmt.defaultColor)
      const txt = empty ? '' : String(value)
      return color
        ? <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, background: softBg(color), color: C.text }}>{txt}</span>
        : <span>{txt}</span>
    }

    case 'date':
      return <span>{formatDateValue(value, fmt.datePattern)}</span>

    case 'relative_time':
      return <span title={formatDateValue(value, 'dd-MMM-yyyy HH:mm')}>{relativeTime(value)}</span>

    case 'elapsed':
      return <ElapsedCell value={value} fmt={fmt} row={row} columns={columns} />

    case 'date_range': {
      const end = readCompanion(fmt.endColumn, row, columns)
      const ds = parseDate(value), de = parseDate(end)
      const days = ds && de ? Math.round((de.getTime() - ds.getTime()) / 86400000) : null
      const pat = fmt.datePattern || 'd MMM'
      return (
        <span>{formatDateValue(value, pat)} – {formatDateValue(end, pat)}
          {days != null && <span style={{ color: C.muted, fontSize: '0.82em' }}> ({days}d)</span>}
        </span>
      )
    }

    case 'countdown': {
      const due = parseDate(value)
      if (!due) return <span style={{ color: C.muted }}>—</span>
      const ms = due.getTime() - new Date().getTime()
      const days = ms / 86400000
      const warn = fmt.warnDays ?? 3
      const danger = fmt.dangerDays ?? 1
      const color = ms < 0 ? NAMED_COLORS.red : days <= danger ? NAMED_COLORS.red : days <= warn ? NAMED_COLORS.amber : NAMED_COLORS.green
      return <span style={{ color, fontWeight: 600 }} title={formatDateValue(value, 'dd-MMM-yyyy HH:mm')}>{humanizeDelta(ms)}</span>
    }

    case 'telephone': {
      if (empty) return <span />
      const num = String(value)
      const country = fmt.showFlag !== false ? countryFromPhone(num) : undefined
      const flag = country ? flagEmoji(country.iso2) : ''
      return (
        <a href={`tel:${num.replace(/[^0-9+]/g, '')}`} onClick={(e) => e.stopPropagation()}
          title={country ? country.name : undefined}
          style={{ color: C.accent, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {flag ? <span style={{ fontSize: '1.05em' }}>{flag}</span> : <span>📞</span>}{fmt.label || num}
        </a>
      )
    }

    case 'country': {
      if (empty) return <span />
      const country = resolveCountry(value)
      const flag = country ? flagEmoji(country.iso2) : ''
      const mode = fmt.countryShow || 'flag_name'
      const text = !country
        ? String(value)
        : mode === 'flag' ? '' : mode === 'flag_code' ? country.iso2 : country.name
      return (
        <span title={country ? country.name : undefined} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {flag ? <span style={{ fontSize: '1.15em', lineHeight: 1 }}>{flag}</span> : <span style={{ color: C.muted }}>🏳️</span>}
          {text && <span>{text}</span>}
        </span>
      )
    }

    case 'email': {
      if (empty) return <span />
      const addr = String(value)
      return (
        <a href={`mailto:${addr}`} onClick={(e) => e.stopPropagation()}
          style={{ color: C.accent, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span>✉️</span>{fmt.label || addr}
        </a>
      )
    }

    case 'url': {
      const href = fmt.hrefColumn ? String(readCompanion(fmt.hrefColumn, row, columns) ?? '') : String(value ?? '')
      if (!href) return <span />
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
          style={{ color: C.accent, textDecoration: 'underline', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span>🔗</span>{fmt.label || (fmt.hrefColumn ? String(value ?? href) : href)}
        </a>
      )
    }

    case 'image': {
      if (empty) return <span />
      return <img src={String(value)} alt="" style={{ height: 28, width: 28, objectFit: 'cover', borderRadius: 4, verticalAlign: 'middle' }} />
    }

    case 'file': {
      if (empty) return <span />
      const href = String(value)
      const name = fmt.label || href.split('/').pop() || href
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
          style={{ color: C.accent, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span>📎</span>{name}
        </a>
      )
    }

    case 'qr':
      return empty ? <span /> : <QrCell value={String(value)} />

    case 'map': {
      if (empty) return <span />
      const q = encodeURIComponent(String(value))
      return (
        <a href={`https://www.google.com/maps/search/?api=1&query=${q}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
          style={{ color: C.accent, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span>📍</span>{fmt.label || String(value)}
        </a>
      )
    }

    case 'copy':
      return empty ? <span /> : <CopyCell value={String(value)} />

    case 'case':
      return <span>{transformCase(empty ? '' : String(value), fmt.textCase)}</span>

    case 'truncate': {
      const s = empty ? '' : String(value)
      const n = fmt.truncate ?? 40
      const short = s.length > n ? s.slice(0, n).trimEnd() + '…' : s
      return <span title={s.length > n ? s : undefined}>{short}</span>
    }

    case 'masked':
      return empty ? <span /> : <MaskedCell value={String(value)} fmt={fmt} />

    case 'template':
      return <span>{applyTemplate(fmt.template || '{value}', value, row, columns)}</span>

    case 'two_line': {
      const secondary = readCompanion(fmt.secondaryColumn, row, columns)
      return (
        <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.2 }}>
          <span style={{ fontWeight: 600 }}>{empty ? '' : String(value)}</span>
          {secondary != null && secondary !== '' && <span style={{ fontSize: '0.8em', color: C.muted }}>{String(secondary)}</span>}
        </span>
      )
    }

    default:
      return <span>{empty ? '' : String(value)}</span>
  }
}

// ── Sub-renderers needing local state ────────────────────────────────────────

function CopyCell({ value }: { value: string }): React.ReactElement {
  const [done, setDone] = useState(false)
  const copy = (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      navigator.clipboard?.writeText(value)
      setDone(true)
      setTimeout(() => setDone(false), 1200)
    } catch { /* clipboard unavailable */ }
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span>{value}</span>
      <button onClick={copy} title="Copy" style={{ border: 'none', background: 'none', cursor: 'pointer', color: done ? NAMED_COLORS.green : C.muted, fontSize: '0.9em', padding: 0 }}>
        {done ? '✓' : '⧉'}
      </button>
    </span>
  )
}

function MaskedCell({ value, fmt }: { value: string; fmt: DisplayFormat }): React.ReactElement {
  const [shown, setShown] = useState(false)
  return (
    <span onClick={(e) => { e.stopPropagation(); setShown((s) => !s) }} title="Click to reveal" style={{ cursor: 'pointer', fontFamily: 'ui-monospace, monospace' }}>
      {shown ? value : maskValue(value, fmt)}
    </span>
  )
}

function QrCell({ value }: { value: string }): React.ReactElement {
  let matrix
  try {
    matrix = encodeQr(value, 'M')
  } catch {
    return <span style={{ color: C.muted }}>QR?</span>
  }
  const { size, modules } = matrix
  const quiet = 2
  const dim = size + quiet * 2
  const rects: React.ReactElement[] = []
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      if (modules[y][x]) rects.push(<rect key={`${x}-${y}`} x={x + quiet} y={y + quiet} width={1} height={1} fill="#000" />)
    }
  }
  return (
    <svg width={56} height={56} viewBox={`0 0 ${dim} ${dim}`} style={{ background: '#fff', verticalAlign: 'middle' }} shapeRendering="crispEdges">
      {rects}
    </svg>
  )
}

// ── leaf helpers ─────────────────────────────────────────────────────────────

function heatmapColor(n: number | null, fmt: DisplayFormat): string {
  if (n == null) return C.muted
  const colors = (fmt.colors && fmt.colors.length ? fmt.colors : ['#16a34a', '#eab308', '#f59e0b', '#dc2626']).map(resolveColor)
  if (fmt.scale === 'gradient') {
    const min = fmt.min ?? 0, max = fmt.max ?? 100
    const t = clamp01((n - min) / ((max - min) || 1))
    return lerpColor(colors[0], colors[colors.length - 1], t)
  }
  const thr = fmt.thresholds && fmt.thresholds.length ? fmt.thresholds : [25, 50, 75]
  let band = 0
  for (let i = 0; i < thr.length; i++) { if (n >= thr[i]) band = i + 1 }
  return colors[Math.min(band, colors.length - 1)]
}

function lerpColor(a: string, b: string, t: number): string {
  const pa = hexToRgb(a), pb = hexToRgb(b)
  if (!pa || !pb) return a
  const r = Math.round(pa[0] + (pb[0] - pa[0]) * t)
  const g = Math.round(pa[1] + (pb[1] - pa[1]) * t)
  const bl = Math.round(pa[2] + (pb[2] - pa[2]) * t)
  return `rgb(${r}, ${g}, ${bl})`
}

function hexToRgb(h: string): [number, number, number] | null {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(h.trim())
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : null
}

function avatarColor(seed: string): string {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) % 360
  return `hsl(${h}, 55%, 45%)`
}

function parseSeries(value: unknown): number[] {
  if (Array.isArray(value)) return value.map((v) => Number(v)).filter((n) => Number.isFinite(n))
  if (typeof value === 'string') {
    const s = value.trim()
    if (s.startsWith('[')) { try { const a = JSON.parse(s); return Array.isArray(a) ? a.map(Number).filter(Number.isFinite) : [] } catch { /* fallthrough */ } }
    return s.split(/[,\s]+/).map(Number).filter((n) => Number.isFinite(n))
  }
  return []
}

function relativeTime(value: unknown): string {
  const d = parseDate(value)
  if (!d) return value == null ? '' : String(value)
  return humanizeDelta(d.getTime() - new Date().getTime())
}

function humanizeDelta(ms: number): string {
  const past = ms < 0
  const abs = Math.abs(ms)
  const mins = Math.round(abs / 60000)
  const hours = Math.round(abs / 3600000)
  const days = Math.round(abs / 86400000)
  let body: string
  if (mins < 1) return 'now'
  if (mins < 60) body = `${mins}m`
  else if (hours < 24) body = `${hours}h`
  else if (days < 30) body = `${days}d`
  else if (days < 365) body = `${Math.round(days / 30)}mo`
  else body = `${Math.round(days / 365)}y`
  return past ? `${body} ago` : `in ${body}`
}
